import BILLING_API from 'api/billing/v1'
import axios from 'axios'
import clsx from 'clsx'
import renderAmountField from 'components/FormControls/amountField'
import { Button, Spinner, Typography } from 'gu-ui-kit'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Field, formValueSelector, reduxForm } from 'redux-form'
import validator from 'utils/asyncValidator'
import formatMoney from 'utils/formatMoney'
import shouldAsyncValidate from 'utils/shouldAsyncValidate'
import * as Yup from 'yup'
import { REFUND_CREATE } from 'modules/Services/types'
import styles from 'modules/Forms/Account/Refund/refund.module.scss'
import formStyles from 'modules/Forms/common-form.module.scss'

const validationSchema = Yup.object().shape({})

let RefundForm = ({ handleSubmit, submitting, networkType, onClose, accountId, form }) => {
  let axiosCancelToken

  const selector = formValueSelector(form)
  const [helperText, setHelperText] = useState()
  const { t } = useTranslation()

  const { amount, balance } = useSelector((state) => ({
    amount: selector(state, 'amount'),
    balance: state['services/network-account'].balance,
  }))

  const calculate = useCallback(
    (amount) => {
      if (axiosCancelToken) {
        axiosCancelToken.cancel()
      }

      axiosCancelToken = axios.CancelToken.source()

      setHelperText(
        <span>
          {t('services::forms.refund.fields.amount.helperText')}
          {'   '}
          <Spinner
            width={12}
            className={styles['refund-amount__spinner']}
          />
        </span>,
      )

      BILLING_API.calculateRefund({
        cancelToken: axiosCancelToken.token,
        accountId,
        data: { amount },
      })
        .then(({ response, error }) => {
          if (error) {
            throw new Error(error.canceled ? 'calculation canceled' : error.message)
          }
          return response.data
        })
        .then(({ refund_amount }) => {
          setHelperText(
            !!parseFloat(refund_amount.value) > 0 ? (
              <span>
                {t('services::forms.refund.fields.amount.helperText')}{' '}
                <span
                  className={styles['refund-amount']}
                  dangerouslySetInnerHTML={{
                    __html: `${formatMoney(refund_amount.value, refund_amount.currency)}`,
                  }}
                />
              </span>
            ) : null,
          )
        })
        .catch((error) => console.logXHR(error))
    },
    [setHelperText, axiosCancelToken],
  )

  const submit = useCallback(
    (values, dispatch) =>
      new Promise((resolve, reject) => {
        dispatch({
          meta: { resolve, reject },
          type: REFUND_CREATE,
          payload: {
            accountId,
            data: values,
            formName: form,
          },
        })
      }).then(onClose),
    [form, accountId],
  )

  useEffect(() => {
    if (!amount) {
      return
    }

    calculate(amount)
  }, [amount])

  return (
    <form
      onSubmit={handleSubmit(submit)}
      noValidate
    >
      <Typography
        variant={'Body-15'}
        className={styles['balance-amount__label']}
        component={'div'}
      >
        {t('services::forms.refund.account_balance')}
      </Typography>

      {balance?.amount !== null ? (
        <Typography
          className={clsx(styles['balance-amount'], 'margin-bottom-24')}
          variant={'Header-70'}
          component={'div'}
          dangerouslySetInnerHTML={{
            __html: formatMoney(balance.amount, balance.currency),
          }}
        />
      ) : (
        <h1>
          <Spinner
            width={40}
            color={'Interface-Seventh'}
          />
        </h1>
      )}

      <div className={'margin-bottom-16'}>
        <Field
          label={t('services::forms.refund.fields.amount.label')}
          name="amount"
          type="text"
          component={renderAmountField}
          t={t}
          fullWidth
          autocomplete="off"
          format={(value) => (value === null ? '' : '' + value)}
          parse={(value) => value.replace(/\s/g, '').replace(',', '.')}
          helperText={helperText}
          simpleHelperStyle
          InputProps={{
            endAdornment: (
              <Typography
                variant={'Body-25'}
                noMargin
                color={'Label-Secondary'}
              >
                RUB
              </Typography>
            ),
          }}
        />
      </div>

      <div className={clsx(formStyles['formControl__action-buttons'], styles['action-buttons'])}>
        <Button
          disabled={submitting}
          onClick={onClose}
          variant={'ghost'}
          grey
          color={'Label-Secondary'}
          size="l"
          type="button"
        >
          {t('common::buttons.cancel')}
        </Button>
        <Button
          disabled={submitting}
          size="l"
          variant="primary"
          type="submit"
        >
          {t('services::forms.refund.action_buttons.submit')}
        </Button>
      </div>
      <Typography
        variant={'Body-15'}
        noMargin
        dangerouslySetInnerHTML={{
          __html: t('services::forms.refund.rules', {
            numberOfDays: networkType === 'yd' ? 10 : 3,
          }),
        }}
      />
    </form>
  )
}

RefundForm = reduxForm({
  form: 'RefundForm',
  initialValues: {
    amount: 0,
  },
  shouldAsyncValidate: shouldAsyncValidate,
  asyncValidate: validator(validationSchema),
})(RefundForm)

RefundForm.propTypes = {
  handleSubmit: PropTypes.func,
  submitting: PropTypes.bool,
  onClose: PropTypes.func,
  autofill: PropTypes.func,
  accountId: PropTypes.string,
  initialValues: PropTypes.object,
  form: PropTypes.string,
  networkType: PropTypes.string,
}

export default RefundForm
